import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Space,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Steps,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  BankOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries, useWisecureClient } from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { CommuneMap } from '@/components/CommuneMap';
import { CommuneCoverageStats } from '@/components/CommuneCoverageStats';
import { neutral, primary } from '@/theme';
import type { PaginatedResponse, DashboardMunicipalityListItem } from '@/types/dashboard';

const { Text } = Typography;

interface GeoRegionItem {
  id: string;
  code: string;
  name: string;
  type: string;
}

const STEP_FIELDS = [
  ['name', 'description', 'firstName', 'lastName', 'email', 'password'],
  ['geoRegionIds'],
] as const;

export function MunicipalitiesPage() {
  const navigate = useNavigate();
  const invalidate = useInvalidateQueries();
  const client = useWisecureClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [geoSearch, setGeoSearch] = useState('');
  const [selectedGeoIds, setSelectedGeoIds] = useState<string[]>([]);
  const [selectedGeoItems, setSelectedGeoItems] = useState<GeoRegionItem[]>([]);
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [geojsonLoading, setGeojsonLoading] = useState(false);
  const [form] = Form.useForm();

  const { data: municipalitiesResponse, isLoading } = useWisecureQuery<
    PaginatedResponse<DashboardMunicipalityListItem>
  >(
    queryKeys.dashboardMunicipalities,
    (c) =>
      c.municipalities.municipalityControllerFindAll({
        limit: 100,
      }) as Promise<PaginatedResponse<DashboardMunicipalityListItem>>,
  );

  const { data: geoRegionsData, isLoading: geoLoading } = useWisecureQuery<{
    data: GeoRegionItem[];
  }>(
    queryKeys.geoRegions(geoSearch),
    (c) =>
      c.geoRegions.geoRegionsControllerFindAll({
        limit: 50,
        type: 'COMMUNE',
        search: geoSearch || undefined,
      }) as Promise<{ data: GeoRegionItem[] }>,
    {
      enabled: !!geoSearch,
    },
  );

  const fetchGeoJSON = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) {
        setGeojson(null);
        return;
      }
      setGeojsonLoading(true);
      try {
        const result = await client.geoRegions.geoRegionsControllerFindByIdsGeoJson({ requestBody: { ids } });
        setGeojson(result);
      } catch {
        setGeojson(null);
      } finally {
        setGeojsonLoading(false);
      }
    },
    [client],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchGeoJSON(selectedGeoIds), 300);
    return () => clearTimeout(timer);
  }, [selectedGeoIds, fetchGeoJSON]);

  useEffect(() => {
    if (!createOpen) return;

    if (selectedGeoIds.length > 0) {
      client.geoRegions.geoRegionsControllerFindByIds({ requestBody: { ids: selectedGeoIds } }).then((items) => {
        setSelectedGeoItems(items as GeoRegionItem[]);
      });
    } else {
      setSelectedGeoItems([]);
    }
  }, [selectedGeoIds, client, createOpen]);

  const createMutation = useWisecureMutation(
    (c, values: Record<string, unknown>) =>
      c.municipalities.municipalityControllerCreate({
        requestBody: values as unknown as import('@wisecure/api-client').CreateMunicipalityDto,
      }),
    {
      onSuccess: () => {
        message.success('Municipalité créée');
        invalidate(queryKeys.dashboardMunicipalities);
        handleClose();
      },
      onError: (error: { body?: { message?: string }; message?: string }) => {
        const msg = error?.body?.message ?? error?.message;
        if (msg?.includes('already exists')) {
          message.error('Un utilisateur avec cet email existe déjà');
        } else {
          message.error('Erreur lors de la création de la municipalité');
        }
      },
    },
  );

  const deleteMutation = useWisecureMutation<unknown, string>(
    (c, id) => c.municipalities.municipalityControllerRemove({ id }),
    {
      onSuccess: () => {
        message.success('Municipalité supprimée');
        invalidate(queryKeys.dashboardMunicipalities);
      },
      onError: () => message.error('Erreur lors de la suppression'),
    },
  );

  const handleClose = () => {
    setCreateOpen(false);
    setCurrentStep(0);
    form.resetFields();
    setSelectedGeoIds([]);
    setGeojson(null);
  };

  const handleNext = async () => {
    try {
      await form.validateFields([...STEP_FIELDS[0]]);
      setCurrentStep(1);
    } catch {
      // validation errors are shown inline by Ant Design
    }
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  const handleCreate = () => {
    form.validateFields().then(() => {
      const allValues = form.getFieldsValue(true);
      createMutation.mutate(allValues);
    });
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: DashboardMunicipalityListItem) => (
        <a onClick={() => navigate(`/municipalities/${record.id}`)}>{name}</a>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_: unknown, record: DashboardMunicipalityListItem) => record.user?.email ?? '—',
    },
    {
      title: 'Communes',
      key: 'communes',
      render: (_: unknown, record: DashboardMunicipalityListItem) => (
        <span>{record.geoRegions?.length ?? 0}</span>
      ),
    },
    {
      title: 'Communes assignées',
      key: 'communeNames',
      render: (_: unknown, record: DashboardMunicipalityListItem) =>
        record.geoRegions?.length > 0
          ? record.geoRegions.slice(0, 3).map((gr) => (
              <Tag key={gr.id} style={{ marginBottom: 2 }}>
                {gr.name}
              </Tag>
            )).concat(
              record.geoRegions.length > 3
                ? [<Tag key="more">+{record.geoRegions.length - 3}</Tag>]
                : [],
            )
          : '—',
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: DashboardMunicipalityListItem) => (
        <Space>
          <Button size="small" onClick={() => navigate(`/municipalities/${record.id}`)}>
            Voir
          </Button>
          <Popconfirm
            title="Supprimer la municipalité ?"
            description="L'utilisateur sera conservé mais la municipalité sera supprimée."
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button danger size="small">
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const geoRegionOptions = useMemo(() => {
    const options = selectedGeoItems.map((gr) => ({
      value: gr.id,
      label: `${gr.name} (${gr.code})`,
    }));

    if (geoRegionsData?.data) {
      for (const gr of geoRegionsData.data) {
        if (!options.some((o) => o.value === gr.id)) {
          options.push({
            value: gr.id,
            label: `${gr.name} (${gr.code})`,
          });
        }
      }
    }

    return options;
  }, [geoRegionsData, selectedGeoItems]);

  return (
    <div>
      <PageHeader
        title="Municipalités"
        subtitle="Gérer les comptes municipalités"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Ajouter une municipalité
          </Button>
        }
      />

      <Table
        columns={columns}
        dataSource={municipalitiesResponse?.data ?? []}
        loading={isLoading}
        rowKey="id"
      />

      <Modal
        open={createOpen}
        onCancel={handleClose}
        width={currentStep === 1 ? 960 : 560}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              {currentStep === 1 && (
                <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                  Précédent
                </Button>
              )}
            </div>
            <Space>
              <Button onClick={handleClose}>Annuler</Button>
              {currentStep === 0 ? (
                <Button type="primary" icon={<ArrowRightOutlined />} onClick={handleNext}>
                  Suivant
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={createMutation.isPending}
                  onClick={handleCreate}
                >
                  Créer la municipalité
                </Button>
              )}
            </Space>
          </div>
        }
      >
        <div style={{ padding: '8px 0 24px' }}>
          <Steps
            current={currentStep}
            size="small"
            items={[
              { title: 'Informations', icon: <BankOutlined /> },
              { title: 'Couverture territoriale', icon: <EnvironmentOutlined /> },
            ]}
          />
        </div>

        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed) => {
            if (changed.geoRegionIds) {
              setSelectedGeoIds(changed.geoRegionIds);
            }
          }}
        >
          {currentStep === 0 && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: neutral[500],
                  }}
                >
                  Municipalité
                </Text>
                <div
                  style={{
                    marginTop: 12,
                    padding: '16px 20px',
                    background: neutral[50],
                    borderRadius: 10,
                    border: `1px solid ${neutral[100]}`,
                  }}
                >
                  <Form.Item
                    name="name"
                    label="Nom de la municipalité"
                    rules={[{ required: true, message: 'Le nom est requis' }]}
                    style={{ marginBottom: 16 }}
                  >
                    <Input placeholder="Ex: Mairie de Paris" size="large" />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label="Description"
                    style={{ marginBottom: 0 }}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder="Description de la municipalité (optionnel)"
                    />
                  </Form.Item>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: neutral[500],
                  }}
                >
                  Personne de contact
                </Text>
                <div
                  style={{
                    marginTop: 12,
                    padding: '16px 20px',
                    background: neutral[50],
                    borderRadius: 10,
                    border: `1px solid ${neutral[100]}`,
                  }}
                >
                  <Space size={16} style={{ display: 'flex', marginBottom: 16 }}>
                    <Form.Item
                      name="firstName"
                      label="Prénom"
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Jean" />
                    </Form.Item>
                    <Form.Item
                      name="lastName"
                      label="Nom"
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Dupont" />
                    </Form.Item>
                  </Space>
                </div>
              </div>

              <div>
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: neutral[500],
                  }}
                >
                  Compte utilisateur
                </Text>
                <div
                  style={{
                    marginTop: 12,
                    padding: '16px 20px',
                    background: neutral[50],
                    borderRadius: 10,
                    border: `1px solid ${neutral[100]}`,
                  }}
                >
                  <Form.Item
                    name="email"
                    label="Adresse email"
                    rules={[
                      { required: true, message: "L'email est requis" },
                      { type: 'email', message: 'Email invalide' },
                    ]}
                    style={{ marginBottom: 16 }}
                    extra="Sera utilisée comme identifiant de connexion"
                  >
                    <Input placeholder="municipalite@example.com" />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="Mot de passe"
                    rules={[
                      { required: true, message: 'Le mot de passe est requis' },
                      { min: 8, message: '8 caractères minimum' },
                    ]}
                    style={{ marginBottom: 0 }}
                    extra="Minimum 8 caractères"
                  >
                    <Input.Password placeholder="••••••••" />
                  </Form.Item>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <div
                style={{
                  marginBottom: 20,
                  padding: '16px 20px',
                  background: primary[50],
                  borderRadius: 10,
                  border: `1px solid ${primary[100]}`,
                }}
              >
                <Text style={{ color: primary[600], fontSize: 13 }}>
                  Sélectionnez les communes qui composent le territoire de cette municipalité.
                  La carte se met à jour automatiquement.
                </Text>
              </div>

              <Form.Item
                name="geoRegionIds"
                label="Communes à assigner"
                rules={[{ required: true, message: 'Au moins une commune est requise' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Tapez pour rechercher des communes..."
                  showSearch
                  filterOption={false}
                  onSearch={setGeoSearch}
                  loading={geoLoading}
                  options={geoRegionOptions}
                  notFoundContent={geoLoading ? 'Chargement...' : 'Aucune commune trouvée'}
                  optionFilterProp="label"
                  size="large"
                  maxTagCount="responsive"
                />
              </Form.Item>

              {selectedGeoIds.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <CommuneCoverageStats features={(geojson?.features ?? []) as unknown as Array<{ properties: { id: string; code: string; name: string; areaKm2: number } }>} />
                  <div style={{ marginTop: 16 }}>
                    <CommuneMap geojson={geojson} loading={geojsonLoading} />
                  </div>
                </div>
              )}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
}
